/**
 * Checks, at compile time, that a certain situation cannot happen (and fail the build if it can). This is useful for
 * checking that all possible cases of a union type are handled.
 *
 * Typical usage pattern:
 *
 *   type X = 'A' | 'B'
 *
 *   function f(x: X) {
 *     if (x === 'a') {
 *       // do something ...
 *       return
 *     }
 *     if (x === 'b') {
 *       // do something ...
 *       return
 *     }
 *     shouldNeverHappen(x)
 *   }
 *
 *
 * If we ever change X such that it has a third case (as in `type X = 'A' | 'B' | 'C') we will get a compile error
 * unless we add logic to this function (`f()`) to handle it.
 *
 * @param n a value of type `never`
 */
export function shouldNeverHappen(n: never): never {
  return n
}

/**
 * An always-failing function. If it ever gets called, it will throw an error. It is useful in conditional expressions
 * in which one of the branches of the expression is the happy path, and the other branch is a sad path in which no
 * value can be computed. Sepcifically, it usually appears as the right-hand-side operand of `??` or `||` expressions.
 *
 * Typical usage pattern:
 *
 *    const dir: string = process.env['WORKING_DIR'] || failMe('missing env variable "a"')
 *
 * Essentially, this is a concise alternative to:
 *
 *    const dir = process.env['a']
 *    if (!dir) {
 *      throw new Error('missing env variable "a"')
 *    }
 *
 * @param hint an optional human-readable string to be placed in the message of the thrown error
 */
export function failMe(hint?: string): never {
  if (!hint) {
    throw new Error(`This expression must never be evaluated`)
  }

  throw new Error(`Bad value: ${hint}`)
}

/**
 * Evaluates just one of several functions (the `cases` parameter) based on the `selector` value passed in.
 * `cases` is a record of zero-argument functions. The function at `cases[selector]` will be evaluated and its return
 * value is returned back to the caller.
 *
 * A compile time error is produced if not all possible values of `selector` are covered by `cases`.
 *
 * Example:
 *    function f(op: '+' | '-' | '*', n1: number, n2: number) {
 *      return switchOn(op, {
 *        '+': () => n1 + n2,
 *        '-': () => n1 - n2,
 *        '*': () => n1 * n2,
 *      })
 *    }
 *
 *
 * The following snippet yields a compile-time error:
 *
 *    function f(op: '+' | '-' | '*', n1: number, n2: number) {
 *      return switchOn(op, {
 *        '+': () => n1 + n2,
 *        '-': () => n1 - n2,
 *      })   // <-- Compiler error here due to missing case ('*')
 *    }
 *
 * And so does this:
 *
 *    function f(op: '+' | '-' | '*', n1: number, n2: number) {
 *      return switchOn(op, {
 *        '+': () => n1 + n2,
 *        '-': () => n1 - n2,
 *        '*': () => n1 - n2,
 *        '/': () => n1 / n2,
 *      })   // <-- Compiler error here due to extraneous case ('/')
 *    }
 *
 * @param selector
 * @param cases
 * @returns
 */
export function switchOn<K extends string, G>(selector: K, cases: Record<K, () => G>): G {
  const f = cases[selector]
  return f()
}

/**
 * An interace describing the structure of typically thrown objects. It follows the structure of the Node's standard
 * error class but it also contains additional properties (such as `reason`) which are commonly found in thrown values.
 * The properties are all optional becuase Node does not require the thrown value to conform to any structure.
 */
interface ErrorLike {
  message?: string
  stack?: string
  reason?: string
}

/**
 * Safely converts an input of type `unknown` into an Error like object. The return value will contain `message`
 * and `stack` properties which will be strings (if a corresponding propertiy of type string exists on the
 * input) or undefined (otherwise). It will also contain an option `reason` property which is not part of the standard
 * `Error` class but is commonly found in thrown error objects.
 *
 * @param err an input of type `unknown`
 * @returns an Error like object, if fallback message is provided, message is set
 */
export function errorLike(err: unknown): ErrorLike
export function errorLike(err: unknown, fallbackMessage: string): ErrorLike & { message: string }
export function errorLike(err: unknown, fallbackMessage?: string): ErrorLike {
  const { message, stack, reason } = (err ?? {}) as ErrorLike
  return {
    message: typeof message === 'string' ? message : fallbackMessage,
    stack: typeof stack === 'string' ? stack : undefined,
    reason: typeof reason === 'string' ? reason : undefined,
  }
}

/**
 * Generates a two-tuple from the arguments passed in. Useful in situations where the compiler fails to infer that
 * `[foo, bar]` is a tuple and treats it like an array. Using `pair(foo, bar)` will make the compiler correctly infer
 * the tuple type.
 */
export function pair<A, B>(a: A, b: B): [A, B] {
  return [a, b]
}

/**
 * Discriminated union type representing either a successful execution result
 * or a caught error with its details.
 *
 * @template T The type of value returned on successful execution
 */
type CatchMeResult<T> =
  | {
      /** Discriminator indicating successful execution */
      success: true
      /** Always undefined for successful execution */
      postmortem: undefined
      /** The value returned by the executed function */
      data: T
    }
  | {
      /** Discriminator indicating execution failure */
      success: false
      /**
       * A flat object containing all enumerable properties of the error object. Always set (when success is false) but
       * may be empty if the thrown value is not an object (e.g., if a string or number was thrown)
       */
      postmortem: Partial<Record<string, unknown>>
      /**
       * The error exactly as it was caught. In JavaScript, the thrown value can be anything - not just Error instances
       * but also strings, numbers, or even undefined (if `throw undefined` was used)
       */
      error: unknown
    }

/**
 * Executes a function and captures its result or error in a structured format. Designed primarily for testing scenarios
 * where you need to examine error details with more precision than Jest's toThrowError() allows, making assertions
 * about specific error properties and their values.
 *
 * Note: In application code errors should propagate naturally to boundaries (like API endpoints) where they can be
 * handled with full context. Hence, this utility should be approached with caution in non-testing code.
 *
 * @param f The function to execute
 * @returns A result object that contains either:
 *    - On error: both the error object (`error`) and a record (`postmortem`) of its enumerable properties
 *    - On success: the executed function return value (`data`); The `postmortem` value is set to `undefined`.
 *
 * @example
 * test('loadData throws an error if file not found', () => {
 *   expect(catchMe(() => loadData('this-file-does-not-exist'))).toMatchObject({
 *     success: false,
 *     postmortem: {
 *       code: 'ENOENT',
 *       errno: -2,
 *       message: "ENOENT: no such file or directory, open 'this-file-does-not-exist'",
 *       path: 'this-file-does-not-exist',
 *       stack: expect.stringMatching(/Error: ENOENT: no such file or directory, open 'this-file-does-not-exist'/),
 *       syscall: 'open',
 *     },
 *   })
 * })
 */
export function catchMe<T>(f: () => T): CatchMeResult<T> {
  try {
    return createSuccess(f())
  } catch (error) {
    return createError<T>(error)
  }
}

export async function catchMeAsync<T>(f: () => Promise<T>): Promise<CatchMeResult<T>> {
  try {
    return createSuccess(await f())
  } catch (error) {
    return createError<T>(error)
  }
}

function createError<T>(error: unknown): CatchMeResult<T> {
  return {
    success: false,
    error,
    postmortem:
      error && typeof error === 'object'
        ? Object.fromEntries(Object.getOwnPropertyNames(error).map(k => [k, (error as Record<string, unknown>)[k]]))
        : {},
  }
}

function createSuccess<T>(data: T): CatchMeResult<T> {
  return {
    success: true,
    data,
    postmortem: undefined,
  }
}
